/**
 * Password Validation Logic
 * Provides cryptographic strength assessment and real-time rule tracking.
 */

// Import regular expression rules used for password validation
import { REGEX } from "./regex";

// Define all supported password rule keys
export type PasswordRuleKey =
    | "length"    // Minimum and maximum length rule
    | "capital"   // At least one uppercase letter
    | "lowercase" // At least one lowercase letter
    | "number"    // At least one numeric digit
    | "special";  // At least one special character

/**
 * PasswordRulesResult: Shape of password rule validation result.
 * Mapped as a boolean record to facilitate real-time UI checklists.
 */
export type PasswordRulesResult = Record<
    PasswordRuleKey,
    boolean        // Each rule returns true or false
>;

/**
 * checkPasswordRules: Evaluates a string against individual strength constraints.
 * Useful for interactive "Password Strength" indicators.
 */
export const checkPasswordRules = (pwd: string) => ({
    // Enforces a secure character range (8-60) to prevent brute-force and buffer issues
    length: pwd.length >= 8 && pwd.length <= 60,
    capital: REGEX.CAPITAL.test(pwd),            // Evaluates uppercase presence
    lowercase: REGEX.LOWERCASE.test(pwd),        // Evaluates lowercase presence
    number: REGEX.NUMBER.test(pwd),              // Evaluates numeric presence
    special: REGEX.SPECIAL.test(pwd),            // Evaluates non-alphanumeric presence
});

/**
 * isPasswordStrong: Logic gate to determine if the entire set of criteria is met.
 */
export const isPasswordStrong = (pwd: string) =>
    Object.values(checkPasswordRules(pwd)).every(Boolean);

/**
 * PASSWORD_RULE_LABELS: UI-friendly labels for displaying password rule status.
 * Standardized mapping for consistent messaging across the security lifecycle.
 */
export const PASSWORD_RULE_LABELS: {
    key: PasswordRuleKey; // Rule identifier
    label: string;        // Human-readable label
}[] = [
        { key: "length", label: "At least 8 characters" },
        { key: "capital", label: "One uppercase letter" },
        { key: "lowercase", label: "One lowercase letter" },
        { key: "number", label: "One number" },
        { key: "special", label: "One special character" },
    ];